/**
 * `<VoxelDisplay>` — render a single static voxel cosmetic.
 *
 * Loads a `vox_json` path via `useVoxelAsset` and mounts an R3F
 * mini-scene scoped to the requested width/height. The camera is
 * auto-fit to the asset's pre-computed bbox so badges (~8×8×4),
 * banners (~32×8×2), and portraits (~16×24×16) all read at the
 * intended scale without per-call tuning.
 *
 * Plan reference: docs/dev/visual-engine/voxel-cosmetics-plan.md §4
 * (W2 component inventory) + §3 (asset schema).
 *
 * Consumer surfaces: storefront cards, cosmetic detail page,
 * AccountPage owned-cosmetics list, EquipCosmeticDialog, profile
 * header portraits.
 */

import { Suspense, useMemo } from "react";
import { Canvas } from "@react-three/fiber";
import { OrbitControls, PerspectiveCamera } from "@react-three/drei";
import { ACESFilmicToneMapping, PCFSoftShadowMap, SRGBColorSpace } from "three";

import { Lights } from "../scene/Lights";
import type { MaterialSlot } from "../voxel/types";
import { VoxelMesh } from "./VoxelMesh";
import { useVoxelAsset } from "./useVoxelAsset";
import type { BakedVoxelAsset } from "./types";

export interface VoxelDisplayProps {
  /** Path to a baked `vox_json` asset. */
  path: string;
  /** Pre-loaded asset, for tests / stories that want to skip the fetch. */
  asset?: BakedVoxelAsset;
  /** Optional per-slot palette override (e.g., player accent color). */
  paletteOverride?: Partial<Record<MaterialSlot, string>>;
  /** Mini-scene width in CSS pixels. */
  width?: number;
  /** Mini-scene height in CSS pixels. */
  height?: number;
  /** When true, allow user-driven orbit/zoom (cosmetic detail page). */
  orbitEnabled?: boolean;
  /** Lighting preset — `"magica-voxel"` adds HDRI + bloom + shadows. */
  lighting?: "p1-basic" | "magica-voxel";
  /** Override canvas background. */
  background?: string;
  /** Render-skip / fallback control for the loading state. */
  loadingFallback?: React.ReactNode;
  /** Render-skip / fallback control for the error state. */
  errorFallback?: (error: Error) => React.ReactNode;
}

export function VoxelDisplay({
  path,
  asset: preloaded,
  paletteOverride,
  width = 128,
  height = 128,
  orbitEnabled = false,
  lighting = "magica-voxel",
  background = "#0b1020",
  loadingFallback = null,
  errorFallback,
}: VoxelDisplayProps) {
  // When a preloaded asset is provided (stories, tests), we skip the
  // fetch entirely. Otherwise the hook drives load + cache.
  const fetched = useVoxelAsset(preloaded ? null : path);
  const asset = preloaded ?? fetched.data;

  if (!asset) {
    if (fetched.error) {
      return errorFallback ? <>{errorFallback(fetched.error)}</> : null;
    }
    return <div style={{ width, height }}>{loadingFallback}</div>;
  }

  return (
    <div style={{ width, height }}>
      <VoxelCanvas
        asset={asset}
        paletteOverride={paletteOverride}
        orbitEnabled={orbitEnabled}
        lighting={lighting}
        background={background}
      />
    </div>
  );
}

interface VoxelCanvasProps {
  asset: BakedVoxelAsset;
  paletteOverride?: Partial<Record<MaterialSlot, string>>;
  orbitEnabled: boolean;
  lighting: "p1-basic" | "magica-voxel";
  background: string;
}

function VoxelCanvas({ asset, paletteOverride, orbitEnabled, lighting, background }: VoxelCanvasProps) {
  const palette = useMemo<Record<MaterialSlot, string>>(() => {
    return {
      primary: paletteOverride?.primary ?? asset.slot_palette.primary,
      secondary: paletteOverride?.secondary ?? asset.slot_palette.secondary,
      accent: paletteOverride?.accent ?? asset.slot_palette.accent,
      emissive: paletteOverride?.emissive ?? asset.slot_palette.emissive,
    };
  }, [asset, paletteOverride]);

  // Camera-fit: aim at the bbox center, distance scaled by the longest axis.
  const { center, cameraDistance } = useMemo(() => {
    const { min, max, size } = asset.bbox;
    const voxelScale = 0.025;
    const c = {
      x: ((min.x + max.x) / 2) * voxelScale,
      y: ((min.y + max.y) / 2) * voxelScale,
      z: ((min.z + max.z) / 2) * voxelScale,
    };
    const longest = Math.max(size.x, size.y, size.z) * voxelScale;
    // 35° fov perspective: distance ~ longest / (2·tan(fov/2)) plus padding.
    const dist = (longest / (2 * Math.tan((35 * Math.PI) / 180 / 2))) * 1.6;
    return { center: c, cameraDistance: Math.max(dist, 0.4) };
  }, [asset]);

  const cameraPos: [number, number, number] = [
    center.x + cameraDistance * 0.7,
    center.y + cameraDistance * 0.6,
    center.z + cameraDistance * 1.0,
  ];

  return (
    <Canvas
      shadows={lighting === "magica-voxel" ? { type: PCFSoftShadowMap } : false}
      gl={{
        antialias: true,
        toneMapping: ACESFilmicToneMapping,
        toneMappingExposure: 1,
        outputColorSpace: SRGBColorSpace,
      }}
      style={{ background, width: "100%", height: "100%" }}
      dpr={[1, 2]}
    >
      <PerspectiveCamera makeDefault position={cameraPos} fov={35} />
      <OrbitControls
        enableDamping
        dampingFactor={0.1}
        enablePan={false}
        enableRotate={orbitEnabled}
        enableZoom={orbitEnabled}
        target={[center.x, center.y, center.z]}
      />
      {/* `Lights preset="magica-voxel"` mounts its own Environment
          which lazy-loads an HDR; wrap in Suspense (R3F v9 + drei
          v10). See `scene/Stage.tsx` for the matching pattern. */}
      <Suspense fallback={null}>
        <Lights preset={lighting} />
        <VoxelMesh voxels={asset.voxels} palette={palette} />
      </Suspense>
    </Canvas>
  );
}
